import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ServiceOverview from './components/ServiceOverview';
import AppointmentScheduler from './components/AppointmentScheduler';
import DocumentPreparation from './components/DocumentPreparation';
import LegalEducation from './components/LegalEducation';
import VerificationCenter from './components/VerificationCenter';
import FAQ from './components/FAQ';
import TrustIndicators from './components/TrustIndicators';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const LegalServicesCenter = () => {
  return (
    <>
      <Helmet>
        <title>Centre de Services Juridiques - Kapital States | Notaires & Experts Immobiliers</title>
        <meta 
          name="description" 
          content="Services juridiques complets pour l'immobilier à Abidjan. Notaires partenaires, vérification de propriété, assistance légale pour acheteurs locaux et diaspora. Transactions sécurisées." 
        />
        <meta name="keywords" content="notaire Abidjan, services juridiques immobilier, vérification propriété, transaction sécurisée, diaspora investissement" />
        <link rel="canonical" href="https://kapitalstates.com/legal-services-center" />
      </Helmet>
      
      {/* 
        Le conteneur principal: 
        Fond noir profond (#050505) identique à l'accueil pour une transition sans coupure.
        Zéro bordure.
      */}
      <div className="min-h-screen bg-[#050505] text-white flex flex-col relative overflow-x-hidden border-none outline-none">
        
        {/* Couche d'ambiance lumineuse (Glows subtils, sans lignes dures) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-brand-gold/5 rounded-full blur-[120px]" />
          <div className="absolute top-[20%] right-[-10%] w-[40vw] h-[40vw] bg-blue-600/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] left-[20%] w-[60vw] h-[60vw] bg-emerald-600/5 rounded-full blur-[150px]" />
        </div>

        <div className="relative z-40 border-none shadow-none">
          <Header className="bg-transparent border-none" />
        </div>
        
        {/* 
          Section Héro : 
          On supprime l'ancien dégradé lourd et on le remplace par un fond sombre 
          avec un immense titre et un texte parfaitement centré.
        */}
        <section className="relative z-10 pt-28 pb-20 overflow-hidden border-none outline-none flex flex-col items-center text-center">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            
            {/* Badge Apple-style */}
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-md rounded-full px-5 py-2 mb-8 border-none shadow-lg">
              <Icon name="Shield" size={16} className="text-brand-gold" />
              <span className="text-white/80 text-xs font-system font-bold tracking-widest uppercase">
                Transactions Sécurisées & Conformes
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-heading font-bold text-white mb-6 leading-tight tracking-tighter">
              Centre de Services
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-gold via-yellow-200 to-brand-gold mt-2">
                Juridiques.
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 mb-12 leading-relaxed max-w-3xl mx-auto font-body font-light">
              Un partenariat exclusif avec des notaires certifiés pour démystifier le droit immobilier ivoirien. Accompagnement complet de la vérification à la signature, avec une transparence absolue.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="bg-white text-black hover:bg-gray-200 rounded-full font-bold px-8 shadow-lg hover:scale-105 transition-transform duration-300 border-none"
              >
                Prendre Rendez-vous
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="FileText"
                iconPosition="left"
                className="bg-white/5 text-white hover:bg-white/10 rounded-full font-medium px-8 backdrop-blur-md border-none transition-transform hover:scale-105"
              >
                Guide Juridique
              </Button>
            </div>
          </div>
        </section>

        {/* 
          Sections Composants : 
          Suppression des "homepage-section-glass" qui forçaient des bordures et des fonds grisâtres.
          Les composants enfants devront "flotter" naturellement sur le fond noir.
        */}
        <main className="relative z-20 w-full flex-grow flex flex-col gap-12 pb-20">
          <div className="w-full border-none outline-none">
            <ServiceOverview />
          </div>

          <div className="w-full border-none outline-none">
            <AppointmentScheduler />
          </div>

          <div className="w-full border-none outline-none">
            <VerificationCenter />
          </div>

          <div className="w-full border-none outline-none">
            <DocumentPreparation />
          </div>

          <div className="w-full border-none outline-none">
            <LegalEducation />
          </div>

          <div className="w-full border-none outline-none">
            <TrustIndicators />
          </div>

          <div className="w-full border-none outline-none">
            <FAQ />
          </div>
        </main>

        {/* 
          Call to Action Final : 
          Au lieu d'un gros bloc coloré, une magnifique carte sombre avec un effet lumineux (Glow).
        */}
        <section className="relative z-10 py-16 px-6 lg:px-8 border-none outline-none">
          <div className="max-w-5xl mx-auto bg-[#111111] rounded-3xl p-10 md:p-16 text-center shadow-2xl relative overflow-hidden group">
            {/* Halo de survol */}
            <div className="absolute inset-0 bg-brand-gold/5 blur-3xl opacity-50 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
            
            <div className="relative z-10">
              <div className="w-20 h-20 bg-brand-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-brand-gold/20">
                <Icon name="Scale" size={36} className="text-brand-gold" />
              </div>
              <h2 className="text-3xl lg:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
                Prêt à sécuriser votre transaction ?
              </h2>
              <p className="text-lg text-white/50 mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                Nos experts juridiques vous accompagnent pour un investissement en toute sérénité. Demandez une consultation gratuite pour évaluer vos besoins.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Phone"
                  iconPosition="left"
                  className="bg-brand-gold hover:bg-yellow-400 text-black rounded-full font-bold px-8 shadow-lg shadow-brand-gold/20 hover:scale-105 transition-transform border-none"
                >
                  +225 21 50 00 53 18
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Mail"
                  iconPosition="left"
                  className="bg-white/5 text-white hover:bg-white/10 rounded-full font-medium px-8 border-none transition-transform hover:scale-105"
                >
                  contact@kapitalstates.com
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* 
          Footer Inline :
          Transformé en mode "Dark Premium" sans ligne dure, avec un noir encore plus profond (#030303).
        */}
        <footer className="bg-[#030303] text-white py-16 relative z-10 border-none outline-none">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 mb-6 group cursor-pointer inline-flex">
                  <div className="w-10 h-10 bg-brand-gold rounded-xl flex items-center justify-center shadow-lg shadow-brand-gold/20">
                    <Icon name="Building2" size={20} color="#030303" strokeWidth={2} />
                  </div>
                  <div>
                    <span className="font-heading font-bold text-xl tracking-tight">Kapital States</span>
                    <p className="text-[10px] text-brand-gold font-bold uppercase tracking-widest mt-0.5">Elevating Real Estate</p>
                  </div>
                </div>
                <p className="text-white/40 mb-6 leading-relaxed font-light text-sm max-w-sm">
                  Votre partenaire de confiance pour tous vos besoins juridiques immobiliers à Abidjan. Expertise locale, standards internationaux.
                </p>
                <div className="flex items-center space-x-3 text-white/50 text-sm font-body">
                  <Icon name="MapPin" size={16} className="text-brand-gold" />
                  <span>Cocody-Angré 8ème Tranche, Abidjan - CI</span>
                </div>
              </div>
              
              <div>
                <h4 className="font-heading font-semibold mb-6 text-white tracking-wide">Services</h4>
                <ul className="space-y-3 text-sm text-white/40 font-light">
                  <li className="hover:text-white cursor-pointer transition-colors">Vérification de Propriété</li>
                  <li className="hover:text-white cursor-pointer transition-colors">Transfert de Titre</li>
                  <li className="hover:text-white cursor-pointer transition-colors">Consultation Juridique</li>
                  <li className="hover:text-white cursor-pointer transition-colors">Préparation Documents</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-heading font-semibold mb-6 text-white tracking-wide">Contact</h4>
                <ul className="space-y-3 text-sm text-white/40 font-light">
                  <li className="flex items-center gap-2"><Icon name="Phone" size={14}/> +225 21 50 00 53 18</li>
                  <li className="flex items-center gap-2"><Icon name="Mail" size={14}/> contact@kapitalstates.com</li>
                  <li className="flex items-center gap-2"><Icon name="Clock" size={14}/> Lun-Ven: 8h-18h</li>
                  <li className="flex items-center gap-2 opacity-0 select-none"><Icon name="Clock" size={14}/> Sam: 9h-13h</li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-white/5 mt-16 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-xs text-white/30 font-light">
                © {new Date()?.getFullYear()} Kapital States. Tous droits réservés.
              </p>
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-20"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-white/30 text-xs font-light">Services juridiques certifiés</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LegalServicesCenter;